/**
 * Local block rendering — the replacement for @storyblok/react.
 *
 * The site used to fetch its content from the Storyblok CDN at runtime and
 * render it through the SDK's dynamic component resolver. The subscription is
 * being decommissioned, and the content had always been versioned in this repo
 * anyway (src/content/landing.json), so both jobs are now done locally:
 *
 *   Block            — resolves blok.component -> React component (was StoryblokComponent)
 *   storyblokEditable — no-op kept so component files need no edits beyond the
 *                       import specifier; the editor attributes it used to emit
 *                       only ever did anything inside the Storyblok visual editor.
 *
 * Adding a new block type = add the component to REGISTRY below, exactly as it
 * used to be added to storyblokInit's `components` map.
 */

import React from "react";

// Templates
import Page from "@/components/templates/Page";
import LandingPage from "@/components/templates/LandingPage";

// Organisms
import { Header } from "@/components/organisms/Header";
import { Hero } from "@/components/organisms/Hero";
import { FeatureGrid } from "@/components/organisms/FeatureGrid";
import { ValueProposition } from "@/components/organisms/ValueProposition";
import { BenefitGrid } from "@/components/organisms/BenefitGrid";
import { ProcessSection } from "@/components/organisms/ProcessSection";
import { DifferenceSection } from "@/components/organisms/DifferenceSection";
import { PricingSection } from "@/components/organisms/PricingSection";
import { CTABanner } from "@/components/organisms/CTABanner";
import { Footer } from "@/components/organisms/Footer";
import { ProductSubNav } from "@/components/organisms/ProductSubNav";
import { LogoBar } from "@/components/organisms/LogoBar";
import { AppSuiteSection } from "@/components/organisms/AppSuiteSection";

// Molecules
import { NavItem } from "@/components/molecules/NavItem";
import { FeatureCard } from "@/components/molecules/FeatureCard";
import { BenefitCard } from "@/components/molecules/BenefitCard";
import { PricingCard } from "@/components/molecules/PricingCard";
import { SocialLink } from "@/components/molecules/SocialLink";
import { Logo } from "@/components/molecules/Logo";
import { CTABlock } from "@/components/molecules/CTABlock";
import { FooterLinkColumn } from "@/components/molecules/FooterLinkColumn";
import { ListItem } from "@/components/molecules/ListItem";
import { ProductTab } from "@/components/molecules/ProductTab";
import { ClientLogo } from "@/components/molecules/ClientLogo";
import { AppSuiteProduct } from "@/components/molecules/AppSuiteProduct";

export interface Blok {
  _uid?: string;
  component?: string;
  [key: string]: any;
}

// Loose on purpose: a few molecules (NavItem, FeatureCard…) take the blok's
// fields as flat props and are rendered directly by their parent, never
// resolved dynamically. The SDK's registry was untyped for the same reason.
const REGISTRY: Record<string, React.ComponentType<any>> = {
  // Templates
  page: Page,
  landing_page: LandingPage,

  // Organisms
  header: Header,
  hero: Hero,
  feature_grid: FeatureGrid,
  value_proposition: ValueProposition,
  benefit_grid: BenefitGrid,
  process_section: ProcessSection,
  difference_section: DifferenceSection,
  pricing_section: PricingSection,
  cta_banner: CTABanner,
  footer: Footer,
  product_sub_nav: ProductSubNav,
  logo_bar: LogoBar,
  app_suite_section: AppSuiteSection,

  // Molecules
  nav_item: NavItem,
  feature_card: FeatureCard,
  benefit_card: BenefitCard,
  pricing_card: PricingCard,
  social_link: SocialLink,
  logo: Logo,
  cta_block: CTABlock,
  footer_link_column: FooterLinkColumn,
  list_item: ListItem,
  product_tab: ProductTab,
  client_logo: ClientLogo,
  app_suite_product: AppSuiteProduct,
};

/**
 * Renders one content block by its `component` name.
 * An unknown block renders nothing rather than crashing the page — the same
 * forgiving behaviour the SDK had, so a stale content file can never white-screen
 * the site.
 */
export const Block: React.FC<{ blok: Blok }> = ({ blok }) => {
  if (!blok?.component) return null;

  const Component = REGISTRY[blok.component];
  if (!Component) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[blocks] no component registered for "${blok.component}"`);
    }
    return null;
  }

  return <Component blok={blok} />;
};

/** Kept for source compatibility; the visual editor it fed is gone. */
export const storyblokEditable = (_blok?: Blok): Record<string, never> =>
  ({}) as Record<string, never>;

/** Alias so the old call sites read naturally after the import swap. */
export const StoryblokComponent = Block;

export default Block;
